﻿import { IFilterService } from 'angular';
import * as XLSX from 'xlsx';
import { ErrorInformation } from '../../core/services/errorRedirectService/errorInformation.model';
import { IErrorRedirectService } from '../../core/services/errorRedirectService/errorRedirect.service';
import { IMessageBoxService } from '../../core/services/messageBoxService/messageBox.service';
import { ScreenLockService } from '../../core/services/screenLockService/screenLock.service';
import { IPolicyholderPaymentNotification } from '../../models/models';
import { AdminPaymentGateway } from './adminPayment.gateway';
import { AdminPaymentViewModel } from './adminPayment.viewmodel';
import { IAdminPaymentParams } from './adminPaymentParams';
import { PaymentAccountTypeService } from './paymentAccountType.service';

export class AdminPaymentController {
    private adminPaymentViewModel: AdminPaymentViewModel;
    private adminPaymentForm: ng.IFormController;
    private paymentNotificationForm: ng.IFormController;

    static $inject = ['$stateParams', 'errorRedirectService', 'ValidationService', '$state', '$filter', 'screenLockService', 'adminPayment.gateway', 'messageBoxService','paymentAccountType.service'];

    constructor(
        private $stateParams: IAdminPaymentParams,
        private errorRedirectService: IErrorRedirectService,
        private validationService: any,
        private $state: ng.ui.IStateService,
        private $filter: IFilterService,
        private screenLockService: ScreenLockService,
        private adminPaymentGateway: AdminPaymentGateway,
        private messageBoxService: IMessageBoxService,
        private paymentAccountTypeService: PaymentAccountTypeService
    ) {
        this.activate();
    }

    public arePaymentMethodsAvailableForExport(): boolean {
        if(!this.adminPaymentViewModel) {
            return false;
        }
        const arePaymentMethodsAvailableForExport: boolean =
            (
                this.adminPaymentViewModel.cardPaymentMethods
                && this.adminPaymentViewModel.cardPaymentMethods.length > 0
            )
            ||
            (
                this.adminPaymentViewModel.echeckPaymentMethods
                && this.adminPaymentViewModel.echeckPaymentMethods.length > 0
            );
        return arePaymentMethodsAvailableForExport;
    }

    public export(): void {
        if(!this.arePaymentMethodsAvailableForExport()){
            return;
        }

        var companyHeading = this.adminPaymentViewModel.policyholder.name + ' - ' + this.adminPaymentViewModel.policyholder.billingAccountNumber;
        const wb = XLSX.utils.book_new();
        const data: Array<Array<string>> = [];
        data.push(
            [
                'Company',
                companyHeading
            ]
        );
        data.push(
            [
                'Contact Email',
                this.adminPaymentViewModel.policyholderPaymentNotification.emailAddress
            ]
        );
        data.push([]);
        data.push(
            [
                "Active",
                "Card Number",
                "Card Type",
                "Expiration Date",
                "Notes"
            ]);
        //cardExpiryDate
        this.adminPaymentViewModel.cardPaymentMethods.forEach(card => {
            const active: string = card.autopay ? 'true' : 'false';
            const cardExpiryDate: string = this.$filter('date')(card.cardExpiryDate, 'MM/dd/yy', 'UTC');
            const cardType: string = this.paymentAccountTypeService.creditCardTypeCodeToName(card.cardType);
            const row: Array<string> = [
                active,
                card.cardNumber,
                cardType,
                cardExpiryDate,
                card.note
            ];
            data.push(row);
        });
        data.push([]);
        data.push(
            [
                "Active",
                "Account Number",
                "Routing Number",
                "Account Type",
                "Notes"
            ]);
        this.adminPaymentViewModel.echeckPaymentMethods.forEach(card => {
            const active: string = card.autopay ? 'true' : 'false';
            const accountType: string = this.paymentAccountTypeService.bankAccountTypeCodeToName(card.accountType);
            const row: Array<string> = [
                active,
                card.accountNumber,
                card.routingNumber,
                accountType,
                card.note
            ];
            data.push(row);
        });
        const ws = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, "Payment Methods");
        const filename = `payment-methods-${ this.adminPaymentViewModel.policyholder.name }.xlsx`
            .replace(/[^a-z0-9\-\.]+/gi, '-')
            .toLowerCase();
        XLSX.writeFile(wb, filename);
    }

    private activate() {
        // this is required in order to set up the validation service to work with the form using "controller as" syntax
        this.validationService = new this.validationService({ controllerAs: this });

        this.screenLockService.broadcast(1);

        this.adminPaymentGateway.loadAdminPaymentViewModel(this.$stateParams.policyholderid)
            .then((viewModel: AdminPaymentViewModel) => {
                this.adminPaymentViewModel = viewModel;
                this.makeEditInputsReadOnly();
                this.screenLockService.broadcast(0);
            });
    }

    public editPaymentMethod(): void {
        this.navigateToPaymentMethods();
    }  
    public makePayment(): void {
        if(this.adminPaymentViewModel.isPaymentAmountGreaterThanTotalBalance())
        {
            this.messageBoxService.showConfirmation('Are You Sure ?', 'The requested amount is greater than the total balance remaining on the account.', 'CONTINUE', 'CANCEL')
                .then((result: any) => {
                    this.submitPayment();
                });
            return;
        }

        if(this.adminPaymentViewModel.isPaymentAmountGreaterThanAmountDue())
        {
            this.messageBoxService.showConfirmation('Payment Alert !', 'The amount entered is greater than the current amount due, would you like to cancel or continue to make the payment?', 'CONTINUE', 'CANCEL')
                .then((result: any) => {
                    this.submitPayment();
                });
        }
        else{

            this.messageBoxService.showConfirmation('Submit Payment Request ?', 'Are you sure you want to submit this Payment Request?', 'PAY', 'CANCEL')
                .then((result: any) => {
                    this.submitPayment();
                });
        }
    }

    private submitPayment() {
        if (this.adminPaymentForm.$invalid) {
            // trigger validation so that validation error messages appear on the form
            this.validationService.checkFormValidity(this);
            return;
        }

        this.adminPaymentViewModel.isSubmitted = true;

        this.screenLockService.broadcast(1);

        this.adminPaymentGateway
            .makePayment(this.adminPaymentViewModel)
            .then(
                (viewModel: AdminPaymentViewModel) => {
                    this.$state.go("shellFrame.administrationFrame.adminPaymentReceipt", {
                        decisionType: viewModel.paymentResponse.decisionTypeCode,
                        reasonCode: viewModel.paymentResponse.reasonCode,
                        merchantReferenceCode: viewModel.paymentResponse.merchantReferenceCode,
                        requestId: viewModel.paymentResponse.requestId
                    });
                }
            ).catch((reason: string) => {
                const error = new ErrorInformation();
                error.errorMessage = reason;
                error.errorDetails = reason;
                this.errorRedirectService.redirectToErrorState(error);
            })
            .finally(() => {
                this.screenLockService.broadcast(0);
            });
    }
    
    public navigateToPaymentMethods(): void {
        this.$state.go('shellFrame.administrationFrame.paymentMethodEdit', {policyholderid: this.adminPaymentViewModel.policyholder.id});
    }    

    public goToPaymentMethodSearch(): void {
        this.$state.go('shellFrame.administrationFrame.paymentMethodSearch');
    }

    public editPolicyholderPaymentNotification(): void {
        this.adminPaymentViewModel.paymentNotificationEmailAddressEditable = true;
    }

    public addPolicyholderPaymentNotification(): void {
        if (this.paymentNotificationForm.$invalid || this.isEmailAddressEmpty()|| !this.adminPaymentViewModel.paymentNotificationEmailAddressEditable) {
            // trigger validation so that validation error messages appear on the form
            this.validationService.checkFormValidity(this);
            return;
        }
        this.savePolicyholderPaymentNotification();
        this.makeEditInputsReadOnly();
    }

    private isEmailAddressEmpty()
    {
        if (this.adminPaymentViewModel.policyholderPaymentNotification.emailAddress)
        {
            return false;
        }
        return true;
    }

    private savePolicyholderPaymentNotification(): ng.IPromise<IPolicyholderPaymentNotification>{
        return this.adminPaymentGateway.savePolicyholderPaymentNotification(this.adminPaymentViewModel.policyholderPaymentNotification)
            .then((PolicyholderPaymentNotificationModel: IPolicyholderPaymentNotification) => {
                if (PolicyholderPaymentNotificationModel.emailAddress){
                    this.adminPaymentViewModel.policyholderPaymentNotification = PolicyholderPaymentNotificationModel;
                }
                return this.adminPaymentViewModel.policyholderPaymentNotification;
            });
    }

    public makeEditInputsReadOnly(){
        this.adminPaymentViewModel.paymentNotificationEmailAddressEditable = false;
    }
}